import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'next-themes';

import {
  applyMemberDisplaySize,
  cacheMemberPreferences,
  DEFAULT_MEMBER_PREFERENCES,
  getCachedMemberPreferences,
  getMemberPreferencesCacheKey,
  getMemberPreferences,
  updateMemberAppearance,
  updateMemberPreferences,
} from '@/services/memberPreferences';

const MemberPreferencesContext = createContext({
  account: {},
  preferences: DEFAULT_MEMBER_PREFERENCES,
  loading: false,
  ready: false,
  loadError: '',
  reload: async () => {},
  save: async () => {},
  saveAppearance: async () => {},
});

export const useMemberPreferences = () => useContext(MemberPreferencesContext);

export const MemberPreferencesProvider = ({ children, memberId, authType }) => {
  const { setTheme } = useTheme();
  const cacheKey = useMemo(
    () => getMemberPreferencesCacheKey({ memberId, authType }),
    [authType, memberId],
  );
  const [account, setAccount] = useState({});
  const [preferences, setPreferences] = useState(() => getCachedMemberPreferences(cacheKey));
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');

  const apply = useCallback((response) => {
    const normalized = cacheMemberPreferences(cacheKey, response.preferences);
    setAccount(response.account || {});
    setPreferences(normalized);
    setReady(true);
    setLoadError('');
    setTheme(normalized.appearance);
    applyMemberDisplaySize(normalized.display_size);
    return normalized;
  }, [cacheKey, setTheme]);

  const reload = useCallback(async () => {
    setLoading(true);
    setReady(false);
    setLoadError('');
    try {
      return apply(await getMemberPreferences());
    } catch (requestError) {
      setLoadError(requestError.message || 'Unable to load your preferences.');
      throw requestError;
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    let active = true;
    const cached = getCachedMemberPreferences(cacheKey);
    setReady(false);
    setLoadError('');
    setPreferences(cached);
    setTheme(cached.appearance);
    applyMemberDisplaySize(cached.display_size);
    getMemberPreferences()
      .then((response) => { if (active) apply(response); })
      .catch((requestError) => {
        // Preferences availability must never prevent the member dashboard.
        if (active) setLoadError(requestError.message || 'Unable to load your preferences.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [apply, cacheKey, setTheme]);

  const save = useCallback(async (nextPreferences) => {
    if (!ready) throw new Error('Load your current preferences before saving changes.');
    return apply(await updateMemberPreferences(nextPreferences));
  }, [apply, ready]);

  const saveAppearance = useCallback(async (appearance) => (
    apply(await updateMemberAppearance(appearance))
  ), [apply]);

  const value = useMemo(() => ({
    account, preferences, loading, ready, loadError, reload, save, saveAppearance,
  }), [account, preferences, loading, ready, loadError, reload, save, saveAppearance]);

  return (
    <MemberPreferencesContext.Provider value={value}>
      {children}
    </MemberPreferencesContext.Provider>
  );
};
